/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-catch */
import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { TravelModeInfo } from '../types'
import {
  TRAVEL_RATE_CARD_SETTINGS_HOME_QUERY_KEY,
  TRAVEL_RATE_CARD_SETTINGS_HOME_URL,
  TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY,
  TRAVEL_RATE_CARD_SETTINGS_URL
} from './queryConstants'

export enum TravelRateCardLevel {
  SITE = 'site',
  ROLE = 'role',
  INDIVIDUAL = 'individual'
}

export enum TravelRateCardStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled'
}

export type TravelRateCardSettings = {
  _id?: string
  levelType: TravelRateCardLevel
  siteId?: string
  role?: string
  userId?: string
  status: TravelRateCardStatus
  timePayRate?: number
  mileagePayRate?: number
  maximumPaidBreak?: number
  homeId?: string
  travelModes?: TravelModeInfo[]
}

export const useGetTravelRateCardSettings = () => {
  return useQuery<TravelRateCardSettings[]>(
    [TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY],
    async () => {
      const response = await getJSON({
        url: TRAVEL_RATE_CARD_SETTINGS_URL,
        isRota: true
      })
      return response
    }
  )
}

export const useGetTravelRateCardSettingsHome = () => {
  return useQuery<TravelRateCardSettings>(
    [TRAVEL_RATE_CARD_SETTINGS_HOME_QUERY_KEY],
    async () => {
      const response = await getJSON({
        url: TRAVEL_RATE_CARD_SETTINGS_HOME_URL,
        isRota: true
      })
      return response
    }
  )
}

export const useCreateTravelRateCard = () => {
  const queryClient = useQueryClient()

  const createSettingsFunction = async (
    values: TravelRateCardSettings
  ): Promise<unknown> => {
    return postJSON({
      url: TRAVEL_RATE_CARD_SETTINGS_URL,
      method: 'POST',
      json: {
        levelType: TravelRateCardLevel.SITE,
        status: values.status,
        timePayRate: values.timePayRate || 0,
        mileagePayRate: values.mileagePayRate || 0,
        maximumPaidBreak: values.maximumPaidBreak || 0
      },
      isRota: true
    })
  }

  const { mutateAsync: createSettings, isLoading: isCreating } = useMutation<
    unknown,
    unknown,
    TravelRateCardSettings,
    unknown
  >([TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY], createSettingsFunction, {
    onSuccess: () => {
      queryClient.invalidateQueries([TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY])
    }
  })

  return {
    createSettings,
    isCreating
  }
}

export const useUpdateTravelRateCardSettings = () => {
  const queryClient = useQueryClient()

  const updateSettingsFunction = async (
    values: TravelRateCardSettings
  ): Promise<unknown> => {
    if (!values._id) {
      throw new Error('Rate card ID is required for updates')
    }

    return postJSON({
      url: `${TRAVEL_RATE_CARD_SETTINGS_URL}/${values._id}`,
      method: 'PUT',
      json: {
        levelType: values.levelType,
        status: values.status,
        timePayRate: values.timePayRate ?? 0,
        mileagePayRate: values.mileagePayRate ?? 0,
        maximumPaidBreak: values.maximumPaidBreak ?? 0
      },
      isRota: true
    })
  }

  const { mutateAsync: updateSettings, isLoading: isUpdating } = useMutation<
    unknown,
    unknown,
    TravelRateCardSettings,
    unknown
  >([TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY], updateSettingsFunction, {
    onSuccess: () => {
      queryClient.invalidateQueries([TRAVEL_RATE_CARD_SETTINGS_QUERY_KEY])
    }
  })

  return {
    updateSettings,
    isUpdating
  }
}
